import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useT } from "@/lib/i18n";
import type { ScenarioSummary } from "@/lib/scenarios";
import { CapstoneStar, ScenStatusDot } from "./atoms";

type ScenarioRowProps = {
  scenario: ScenarioSummary;
  // Render as a button (e.g. opens the LockedExplainSheet) instead
  // of a link. LOCKED rows shouldn't navigate to detail — there's
  // nothing meaningful to do there until prereqs clear.
  onClick?: () => void;
};

/**
 * Compact row for journal lists. Renders the status dot, title +
 * level chip + optional capstone marker, and a metadata line that
 * shifts content based on status (LOCKED shows blocking deps,
 * others show counts).
 *
 * Mockup: `design/hifi/scenarios.jsx` `ScenarioRow` lines ~298–327.
 */
export function ScenarioRow({ scenario, onClick }: ScenarioRowProps) {
  const t = useT();
  const status = scenario.progress?.status ?? "AVAILABLE";
  const isHardLocked = status === "LOCKED";

  // Metadata varies by state — design-doc'd in mockup:
  //   LOCKED  → "после: <prereq titles>" (blocking deps)
  //   default → counts + estimated minutes
  // For LOCKED we only have slugs (server doesn't enrich titles
  // into the row payload). The bottom-sheet `LockedExplainSheet`
  // resolves them to titles via individual scenario lookups.
  const metaText = isHardLocked
    ? t("skills.locked_meta_after", { names: scenario.prerequisiteSlugs.join(" · ") })
    : t("skills.scenario_meta", {
        words: scenario.wordCount.toString(),
        rules: scenario.ruleCount.toString(),
        mins: scenario.estimatedMinutes.toString(),
      });

  const inner = (
    <div
      className={cn(
        "flex items-center gap-3 rounded-[var(--r-md)] border bg-surface px-[14px] py-3",
        isHardLocked && "opacity-[0.62]",
      )}
      style={{
        borderColor: scenario.capstone ? "var(--scen-bg2)" : "var(--border)",
      }}
    >
      <ScenStatusDot status={status} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-[14px] font-semibold text-text">{scenario.title}</span>
          <span
            className="shrink-0 rounded px-[5px] py-[1px] font-mono text-[9px] font-semibold text-text-3"
            style={{ background: "var(--surface-2)" }}
          >
            {scenario.level}
          </span>
          {scenario.capstone && (
            <span
              className="inline-flex shrink-0 items-center gap-[3px] rounded px-[5px] py-[1px] font-mono text-[9px] font-bold uppercase tracking-wider"
              style={{ background: "var(--scen-bg)", color: "var(--scen-fg)" }}
            >
              <CapstoneStar size={9} />
              {t("skills.tag_capstone")}
            </span>
          )}
        </div>
        <div className="truncate font-mono text-[11px] text-text-3">{metaText}</div>
      </div>
      {!isHardLocked && <ChevronRight className="size-3.5 shrink-0 text-text-3" />}
    </div>
  );

  if (isHardLocked || onClick) {
    return (
      <button type="button" onClick={onClick} className="block w-full text-left">
        {inner}
      </button>
    );
  }
  return (
    <Link to={`/skills/${scenario.slug}`} className="block">
      {inner}
    </Link>
  );
}
